// Loose loops and one-shots → the producer's private library ("Mine").
//
// One row per file with its guesses filled in (name, loop or one-shot, BPM
// from the name or the loop's length), all editable; a line on where the
// library stands against its quota; the privacy rule said once, where it
// matters. Presentation only — reading is lib/web-upload/, sending is
// hooks/useClipSender.ts, the same path a drop on the shelf takes. Inside
// the library page the page's sender takes the clips (into the folder on
// screen) and the form hands back at once: the page shows the progress.

import { useMemo, useState } from "react";
import { Link } from "react-router";
import { CheckCircle, Loader2, RotateCcw, XCircle } from "lucide-react";
import { UploadProgress } from "@/components/MaterialLibrary";

import { Check } from "lucide-react";
import type { NewTag } from "@shared/api-types.js";
import { tagLabel } from "@shared/library-tags.js";
import { Button } from "@web-shared/components/ui/button";
import { cn } from "@web-shared/lib/utils";
import { barsOf } from "@/lib/web-upload/clip-meta";
import type { Heard } from "@/lib/web-upload/listen";
import { clipsFromFiles, type ReadFile } from "@/lib/web-upload/read-files";
import { suggestForClips } from "@/lib/web-upload/suggest-clips";
import type { ClipToSend } from "@/lib/web-upload/library-upload";
import { useAuthGate } from "../auth";
import { useClipSender, useHostClipSender } from "../hooks/useClipSender";
import { formatBytes, useMyClips, usageText } from "../hooks/useMyClips";
import { useUnsavedGuard } from "../hooks/useUnsavedGuard";

interface Row extends ClipToSend {
  include: boolean;
  bpmText: string;
}

/** Said once, at the point of upload (brief: Phase 2 privacy rule). */
export const LIBRARY_PRIVACY_LINE =
  "Only you can see your library. A clip you place in a project goes wherever the project goes: saved versions, remixes and jams.";

export function ClipsForm({
  files,
  heard = null,
  onBack,
  onDone,
  onHandOff,
}: {
  files: ReadFile[];
  heard?: Heard | null;
  onBack: () => void;
  onDone: () => void;
  /** Inside a host that shows the upload itself (the library page's modal): close once the clips are handed over. */
  onHandOff?: () => void;
}) {
  const gate = useAuthGate();
  const mine = useMyClips();
  const host = useHostClipSender();
  const own = useClipSender(mine);
  const handOff = host !== null && onHandOff !== undefined;
  const sender = handOff ? host.sender : own;
  const upload = sender.upload;
  const adding = sender.adding;
  const [rows, setRows] = useState<Row[]>(() =>
    clipsFromFiles(files, suggestForClips(files, heard)).map((c) => ({ ...c, include: true, bpmText: c.bpm !== null ? String(c.bpm) : "" })),
  );
  /** A tap on a suggested tag keeps it: it goes up confirmed. A tap again lets it go back to a suggestion. */
  const toggleTag = (i: number, tag: string): void =>
    setRows((all) => all.map((r, j) => (j === i ? { ...r, tags: (r.tags ?? []).map((t): NewTag => (t.tag === tag ? { ...t, confirmed: !t.confirmed } : t)) } : r)));
  const set = (i: number, change: Partial<Row>): void => setRows((all) => all.map((r, j) => (j === i ? { ...r, ...change } : r)));

  const included = rows.filter((r) => r.include);
  const addingBytes = included.reduce((n, r) => n + r.probe.uploadBytes, 0);
  const bpmOf = (r: Row): number | null => {
    const v = Number(r.bpmText);
    return r.bpmText.trim() !== "" && Number.isFinite(v) && v >= 20 && v <= 999 ? v : null;
  };
  const badBpm = included.some((r) => r.bpmText.trim() !== "" && bpmOf(r) === null);
  const working = !handOff && (upload.phase.kind === "converting" || upload.phase.kind === "sending" || upload.phase.kind === "finishing");
  const guard = useUnsavedGuard(working, "Your clips are still uploading. Leave and they won't be added.");

  const fitsText = useMemo(() => {
    if (!mine.signedIn || mine.quotaBytes === 0) return null;
    return `Your library: ${usageText(mine.usedBytes, mine.quotaBytes)} used. These add about ${formatBytes(addingBytes)}.`;
  }, [mine.signedIn, mine.usedBytes, mine.quotaBytes, addingBytes]);

  const submit = (): void =>
    gate.requireSignIn("library_add", "Sign in to add clips to your library", () => {
      const clips = included.map((r) => ({ ...r, name: r.name.trim() || r.file.name, bpm: r.kind === "loop" ? bpmOf(r) : null }));
      void sender.sendClips(clips, handOff ? host.into : null, { from: "upload", receipt: handOff });
      if (handOff) onHandOff();
    });
  const again = (): void => {
    upload.reset();
    onDone();
  };

  if (!handOff && upload.phase.kind === "done") {
    const n = upload.phase.clips.length;
    const already = upload.phase.already;
    return (
      <div className="flex flex-col items-center gap-3 rounded-2xl border border-hairline bg-raised p-6 text-center">
        <CheckCircle className="size-8 text-primary" />
        <p className="text-base font-semibold">
          {n > 0 ? `Added ${n} clip${n === 1 ? "" : "s"} to your library` : "Those are already in your library"}
        </p>
        <p className="text-sm text-muted-foreground">
          Open any project and find them under Mine, in Material — or file and tag them in{" "}
          <Link to="/library" className="underline">
            your library
          </Link>
          .
        </p>
        {already.length > 0 && n > 0 && (
          <p className="text-xs text-muted-foreground">{already.length} were already there, so they weren't added twice.</p>
        )}
        <Button variant="outline" onClick={again}>Add more</Button>
      </div>
    );
  }

  // In flight: the same per-file rows the library's shelf shows.
  if (!handOff && upload.phase.kind !== "idle" && adding) {
    return (
      <div className="space-y-3 rounded-2xl border border-hairline bg-raised p-4">
        <p className={cn("flex items-center gap-2 text-sm", adding.error ? "text-removed" : "text-muted-foreground")} role="status">
          {adding.error ? <XCircle className="size-4 shrink-0" /> : <Loader2 className="size-4 animate-spin" />}
          {adding.text}
        </p>
        {adding.progress && <UploadProgress view={adding.progress} />}
        <div className="flex justify-center gap-2">
          {adding.onRetry && (
            <Button size="sm" onClick={adding.onRetry}>
              <RotateCcw className="mr-1 size-3.5" /> Retry
            </Button>
          )}
          {upload.phase.kind === "failed" && (
            <Button size="sm" variant="outline" onClick={upload.reset}>Back</Button>
          )}
        </div>
        <p className="text-center text-xs text-muted-foreground">Keep this tab open until it's done.</p>
        {guard.dialog}
      </div>
    );
  }

  return (
    <div className="space-y-4 rounded-2xl border border-hairline bg-raised p-4">
      <ul className="divide-y divide-border rounded-xl border border-hairline">
        {rows.map((r, i) => {
          const bpm = bpmOf(r);
          return (
            <li key={r.file.path} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-2">
              <input type="checkbox" checked={r.include} aria-label={`Include ${r.name}`} onChange={(e) => set(i, { include: e.target.checked })} />
              <input
                value={r.name}
                onChange={(e) => set(i, { name: e.target.value })}
                aria-label="Clip name"
                className={cn(
                  "min-w-0 flex-1 rounded-md bg-transparent px-1 py-0.5 text-sm hover:bg-white/5 focus:bg-background",
                  !r.include && "text-muted-foreground line-through",
                )}
              />
              <select
                value={r.kind}
                onChange={(e) => set(i, { kind: e.target.value as Row["kind"] })}
                aria-label="Loop or one-shot"
                className="h-7 rounded-md border border-input bg-background px-1.5 text-xs"
              >
                <option value="loop">Loop</option>
                <option value="oneshot">One-shot</option>
              </select>
              <input
                value={r.kind === "loop" ? r.bpmText : ""}
                disabled={r.kind !== "loop"}
                onChange={(e) => set(i, { bpmText: e.target.value })}
                inputMode="decimal"
                placeholder="BPM"
                aria-label="BPM"
                className={cn(
                  "h-7 w-16 rounded-md border bg-background px-1.5 text-xs disabled:opacity-40",
                  r.bpmText !== "" && bpm === null ? "border-removed" : "border-input",
                )}
              />
              <span className="w-24 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                {r.kind === "loop" && bpm !== null ? `${barsOf(r.probe.durationSec, bpm)} bars · ` : ""}
                {r.probe.durationSec < 10 ? `${r.probe.durationSec.toFixed(1)} s` : `${Math.round(r.probe.durationSec)} s`}
              </span>
              {(r.tags?.length ?? 0) > 0 && (
                <span className="flex basis-full flex-wrap items-center gap-1.5 pl-6" aria-label="Suggested tags">
                  {r.tags!.map((t) => (
                    <button
                      key={t.tag}
                      type="button"
                      onClick={() => toggleTag(i, t.tag)}
                      aria-pressed={t.confirmed === true}
                      title={t.confirmed ? "Kept — it goes up as a tag" : "Suggested — tap to keep it"}
                      className={cn(
                        "flex h-6 items-center gap-1 rounded-full border px-2 text-[11px]",
                        t.confirmed ? "border-foreground/40 bg-white/10 text-foreground" : "border-dashed border-primary/50 text-muted-foreground hover:text-foreground",
                      )}
                    >
                      {t.confirmed && <Check className="size-3 text-primary" />}
                      {tagLabel(t.tag)}
                    </button>
                  ))}
                </span>
              )}
            </li>
          );
        })}
      </ul>

      {fitsText && <p className="text-xs text-muted-foreground">{fitsText}</p>}
      <p className="text-xs text-muted-foreground">{LIBRARY_PRIVACY_LINE}</p>

      <div className="flex items-center gap-2">
        <Button variant="ghost" onClick={onBack}>Back</Button>
        <Button className="flex-1" disabled={included.length === 0 || badBpm} onClick={submit}>
          Add {included.length} clip{included.length === 1 ? "" : "s"} to my library
        </Button>
      </div>
    </div>
  );
}
